import { useQuery } from '@apollo/client';
import {
  COMMON_CONSTANTS,
  ContentDeciderContainer,
  Generator,
  getTranslatedText,
  Loader,
  useProduct,
  withErrorBoundary
} from '@nile/components';
import {
  AccountClient,
  GET_PERSONALIZATION_BY_NAME,
  TicketClient
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import TicketTable from '@nile/mangifera/src/Components/Ticket/TicketTable';
import { defaultPersonalization } from '@nile/mangifera/src/Constants/Tickets';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import { getEmoji } from '@nile/mangifera/src/Pebbles/Views';
import {
  getAccessor,
  getDependantField,
  getParentField,
  getTicketFields,
  isDependantField,
  isEditable
} from '@nile/mangifera/src/Pebbles/Favourites';
import { Tag } from '@superops/ui-design-system';
import { getTabTemplate } from '@nile/mangifera/src/Pebbles/TabManager';
import {
  getColumnWidth,
  getType
} from '@nile/mangifera/src/Pebbles/TicketContainer';
import gql from 'graphql-tag';
import { cloneDeep } from 'lodash';
import React, { useCallback, useContext, useState } from 'react';
import sizeMe from 'react-sizeme';
import { SVGIcon } from '@superops/super-icons';
import { useIntl } from 'react-intl';
import {
  WidgetWrapper,
  Header,
  Title,
  Content,
  Actions,
  NumberTitle,
  NumberCardContainer,
  Badge
} from '../HomeWidgetContainer';
import AccessDenied from './AccessDenied';
import DeletedView from './DeletedView';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';

function TicketViewWidget(props) {
  const {
    widget,
    user,
    meta,
    size,
    ticketViews,
    deleteWidget,
    queryVariableInputs,
    isReadOnly = false
  } = props;
  const intl = useIntl();

  const viewPermission = widget?.permission;
  const viewExist = ticketViews.some((el) => el.id === widget.id);
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const context = useContext(HomeContext);
  const tabContext = useContext(TabContext);
  const [loading, setLoading] = useState(!!widget && viewPermission);
  const [personalisation, setPersonalisation] = useState({});
  const [data, setData] = useState([]);
  const [listInfo, setListInfo] = useState({});
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);

  const currentView =
    viewPermission && viewExist
      ? ticketViews.find((el) => el.id === widget?.id)
      : widget;
  const ticketAttributes = meta?.find((item) => {
    return item.name === 'Ticket';
  }).attributes;

  const getVariables = (pageOptions) => {
    const variables = {
      listInfo: {
        value: pageOptions
          ? { ...pageOptions, view: { id: widget.id } }
          : options,
        type: 'ListInfoInput',
        required: true
      }
    };
    return variables;
  };

  const navigateToView = () => {
    if (!viewPermission || !viewExist) return;
    tabContext.openTab({
      module: 'WORKSPACE',
      url: `/ticket/${currentView.id}/ticket-list`
    });
  };

  const options = {
    pageSize: 20,
    page: 1,
    view: { id: widget.id },
    sort: [
      {
        attribute: 'createdTime',
        order: 'DESC'
      }
    ]
  };

  if (queryVariableInputs) {
    options.condition = {
      joinOperator: 'AND',
      operands: [
        {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'displayId',
              operator: 'isNotEmpty',
              value: null
            }
          ]
        },
        {
          joinOperator: 'AND',
          operands: [
            {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'technician.userId',
                  operator: 'is',
                  operatorValue: null,
                  value: queryVariableInputs.getCompanyUser?.userId
                }
              ]
            }
          ]
        },
        {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'createdTime',
              operator: 'inLast',
              operatorValue: null,
              value: {
                quantity: '30',
                unit: 'DAY'
              }
            }
          ]
        }
      ]
    };
  }

  const getActions = (col) => {
    switch (col.name) {
      case 'subject':
        return {
          onClick: context.openTab
        };

      default:
        return {};
    }
  };

  const fetchViewData = async (personalization, pageOptions) => {
    setLoading(true);
    const personalizedColumns = personalization
      ? JSON.parse(personalization)
      : defaultPersonalization(isMSPProduct);
    const generator = new Generator({
      operation: 'getTicketList',
      variables: getVariables(pageOptions),
      fields: getTicketFields(personalizedColumns)
    });
    const queryString = generator.queryBuilder();
    const response = await TicketClient.query({
      query: gql`
        ${queryString.query}
      `,
      variables: {
        listInfo: pageOptions
          ? { ...pageOptions, view: { id: widget.id } }
          : options
      },
      fetchPolicy: 'network-only'
    });
    setLoading(false);
    const cols = personalizedColumns;
    const columns = cols.map((col) => {
      return {
        Header: getTranslatedText(intl, col.label),
        accessor: getAccessor(col),
        type: getType(col),
        minWidth: getColumnWidth(col.name).minWidth,
        width: getColumnWidth(col.name).width,
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        editable: isEditable(col),
        disableSortBy: true,
        disableFilters: true,
        actions: getActions(col),
        field: col,
        parentField: isDependantField(col.name)
          ? ticketAttributes.find((el) => el.name === getParentField(col.name))
          : col,
        dependantField: isDependantField(col.name)
          ? col
          : ticketAttributes.find(
              (el) => el.name === getDependantField(col.name)
            )
      };
    });
    setColumnGroup([{ Header: 'Name', columns }]);
    setData(response?.data?.getTicketList?.tickets || []);
    setListInfo(response?.data?.getTicketList?.listInfo);
  };

  useQuery(GET_PERSONALIZATION_BY_NAME, {
    variables: {
      userId: user.userId,
      category: 'TicketView',
      name: widget.id
    },
    client: AccountClient,
    skip: !viewPermission,
    onCompleted(res) {
      setLoading(true);
      const personalizationJSON =
        res?.getPersonalizationByCategoryAndName?.value;
      setPersonalisation(personalizationJSON);
      fetchViewData(personalizationJSON);
    },
    onError(err) {
      setLoading(false);
      console.log(err);
    }
  });

  if (loading) {
    return (
      <WidgetWrapper>
        <div className="widget-header flex center fg1">
          <Actions className="widget-actions visibilityActions">
            <div onClick={deleteWidget.bind(this, widget)} className="ptr">
              <SVGIcon name="Delete" />
            </div>
          </Actions>
        </div>
        <Loader />
      </WidgetWrapper>
    );
  }
  return (
    <WidgetWrapper>
      {size.width > 300 && size.height > 200 ? (
        <>
          <Header isReadOnly={isReadOnly}>
            <Title
              isReadOnly={isReadOnly}
              onClick={() => {
                if (!isReadOnly) {
                  navigateToView();
                }
              }}
            >
              <Badge className="mr15">{getEmoji(currentView)}</Badge>
              {currentView?.name}
              {viewPermission && viewExist && (
                <div className="ml-10">
                  <Tag>
                    {data?.length}/{listInfo?.totalCount}
                  </Tag>
                </div>
              )}
            </Title>
            {!isReadOnly && (
              <div className="widget-header flex center fg1">
                <Actions className="widget-actions visibilityActions">
                  <div
                    onClick={deleteWidget.bind(this, widget)}
                    className="ptr"
                  >
                    <SVGIcon name="Delete" />
                  </div>
                </Actions>
              </div>
            )}
          </Header>
          {!viewPermission ? (
            <AccessDenied />
          ) : (
            <>
              {!viewExist ? (
                <DeletedView />
              ) : (
                <Content>
                  <ContentDeciderContainer
                    isLoading={loading}
                    hasContent={
                      !loading &&
                      data?.length > 0 &&
                      columnGroup[0]?.columns.length > 0
                    }
                    fullHeight
                    // error={ticketListError}
                    mainContent={
                      <TicketTable
                        context={context}
                        columns={columnGroup}
                        data={data}
                        setData={setData}
                        rowCount={20}
                        updateMyData={null}
                        refreshTable={() => {}}
                        pagination={false}
                        pages={1}
                        activePage={0}
                        noBulkSelect
                      />
                    }
                    newEmptyContainer
                    loadingContent={<Loader />}
                    notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
                  />
                </Content>
              )}
            </>
          )}
        </>
      ) : (
        <NumberCardContainer>
          <Badge className="icon">{getEmoji(currentView)}</Badge>
          {!isReadOnly && (
            <Actions className="icon visibilityActions">
              <div onClick={deleteWidget.bind(this, widget)} className="ptr">
                <SVGIcon name="Delete" />
              </div>
            </Actions>
          )}
          {!viewPermission ? (
            <AccessDenied small />
          ) : (
            <>
              {!viewExist ? (
                <DeletedView small />
              ) : (
                <div
                  className="content ptr"
                  onClick={() => {
                    if (!isReadOnly) {
                      navigateToView();
                    }
                  }}
                >
                  <NumberTitle>{listInfo?.totalCount}</NumberTitle>
                  {currentView?.name}
                </div>
              )}
            </>
          )}
        </NumberCardContainer>
      )}
    </WidgetWrapper>
  );
}

export default withErrorBoundary(
  sizeMe({ monitorHeight: true })(TicketViewWidget)
);
